/**
 * Romanian glossary terms (draft cluster).
 *
 * Definitions are conceptual and jurisdiction-neutral. No tax rates, legal
 * thresholds, provider names or figures specific to Romania are asserted here.
 */
export interface RoGlossaryTerm {
  term: string;
  group: string;
  definition: string;
}

export const RO_GLOSSARY_GROUPS = [
  "Bazele investițiilor",
  "Instrumente",
  "Costuri",
  "Risc",
  "Piață și tranzacționare",
  "Criptoactive",
  "Credit și bancar",
] as const;

export const RO_GLOSSARY: RoGlossaryTerm[] = [
  // Bazele investițiilor
  {
    term: "Activ",
    group: "Bazele investițiilor",
    definition:
      "Un bun sau un drept care are valoare economică și poate genera venit ori poate fi vândut. În context investițional, se referă la acțiuni, obligațiuni, fonduri, imobiliare sau criptoactive.",
  },
  {
    term: "Portofoliu",
    group: "Bazele investițiilor",
    definition:
      "Totalitatea investițiilor deținute de o persoană. Structura portofoliului, adică ponderea fiecărui tip de activ, influențează riscul mai mult decât alegerea unui instrument individual.",
  },
  {
    term: "Diversificare",
    group: "Bazele investițiilor",
    definition:
      "Distribuirea capitalului între mai multe active, sectoare sau regiuni pentru a reduce impactul unei pierderi izolate. Diversificarea reduce riscul specific, dar nu elimină riscul de piață.",
  },
  {
    term: "Alocarea activelor",
    group: "Bazele investițiilor",
    definition:
      "Decizia privind ponderea fiecărei clase de active în portofoliu. Este determinată de orizontul de timp, de obiectiv și de toleranța la pierdere.",
  },
  {
    term: "Reechilibrare",
    group: "Bazele investițiilor",
    definition:
      "Readucerea ponderilor din portofoliu la valorile-țintă, după ce evoluția pieței le-a modificat. Se face la interval fix sau la depășirea unui prag prestabilit.",
  },
  {
    term: "Orizont de investiție",
    group: "Bazele investițiilor",
    definition:
      "Perioada pentru care banii pot rămâne investiți fără a fi necesari pentru cheltuieli. Un orizont scurt limitează tipurile de active potrivite.",
  },
  {
    term: "Dobândă compusă",
    group: "Bazele investițiilor",
    definition:
      "Mecanismul prin care randamentul obținut se adaugă la capital și generează, la rândul lui, randament. Efectul crește neliniar odată cu perioada.",
  },
  {
    term: "Randament",
    group: "Bazele investițiilor",
    definition:
      "Câștigul sau pierderea unei investiții raportată la suma investită, exprimată de regulă procentual. Randamentul trecut nu garantează rezultate viitoare.",
  },
  {
    term: "Randament anualizat",
    group: "Bazele investițiilor",
    definition:
      "Randamentul mediu pe an care ar produce același rezultat final pe întreaga perioadă. Permite compararea unor investiții cu durate diferite.",
  },
  {
    term: "Randament real",
    group: "Bazele investițiilor",
    definition:
      "Randamentul rămas după scăderea inflației. Un randament nominal pozitiv poate însemna o pierdere de putere de cumpărare.",
  },
  {
    term: "Inflație",
    group: "Bazele investițiilor",
    definition:
      "Creșterea generalizată a prețurilor, care reduce puterea de cumpărare a banilor păstrați în numerar sau în instrumente cu randament scăzut.",
  },
  {
    term: "Investiții periodice (DCA)",
    group: "Bazele investițiilor",
    definition:
      "Strategia de a investi o sumă fixă la intervale regulate, indiferent de preț. Netezește prețul mediu de achiziție, dar nu garantează profit.",
  },

  // Instrumente
  {
    term: "Acțiune",
    group: "Instrumente",
    definition:
      "Titlu care reprezintă o parte din capitalul unei societăți și conferă deținătorului drepturi patrimoniale și, de regulă, de vot.",
  },
  {
    term: "Obligațiune",
    group: "Instrumente",
    definition:
      "Titlu de datorie prin care emitentul se împrumută de la investitor și se obligă la plata unor cupoane și la rambursarea valorii nominale la scadență.",
  },
  {
    term: "Fond mutual",
    group: "Instrumente",
    definition:
      "Vehicul colectiv de investiții care reunește banii mai multor investitori într-un portofoliu administrat profesionist, cu unități emise și răscumpărate de fond.",
  },
  {
    term: "ETF",
    group: "Instrumente",
    definition:
      "Fond tranzacționat la bursă, ale cărui unități se cumpără și se vând în timpul ședinței de tranzacționare, la fel ca o acțiune. De regulă urmărește un indice.",
  },
  {
    term: "Indice bursier",
    group: "Instrumente",
    definition:
      "Un coș de titluri construit după reguli publice, folosit ca reper pentru evoluția unei piețe sau a unui sector.",
  },
  {
    term: "Dividend",
    group: "Instrumente",
    definition:
      "Parte din profitul unei societăți distribuită acționarilor. Plata nu este garantată și poate fi redusă sau suspendată.",
  },
  {
    term: "Randamentul dividendului",
    group: "Instrumente",
    definition:
      "Raportul dintre dividendul anual pe acțiune și prețul acțiunii. Un randament ridicat poate reflecta și o scădere puternică a prețului.",
  },
  {
    term: "Instrument derivat",
    group: "Instrumente",
    definition:
      "Contract a cărui valoare depinde de evoluția unui activ-suport. Poate implica efect de levier și pierderi care depășesc suma investită inițial.",
  },
  {
    term: "Depozit bancar",
    group: "Instrumente",
    definition:
      "Sumă plasată la o instituție de credit pentru o perioadă determinată, cu o dobândă stabilită contractual.",
  },

  // Costuri
  {
    term: "Comision de tranzacționare",
    group: "Costuri",
    definition:
      "Suma percepută de intermediar pentru executarea unui ordin. Poate fi fixă, procentuală sau combinată, cu un minim per ordin.",
  },
  {
    term: "Spread",
    group: "Costuri",
    definition:
      "Diferența dintre prețul de cumpărare și cel de vânzare afișate simultan. Este un cost implicit, suportat chiar și când comisionul afișat este zero.",
  },
  {
    term: "Cost de conversie valutară",
    group: "Costuri",
    definition:
      "Costul aplicat la schimbul dintre moneda contului și moneda activului. Apare la fiecare tranzacție în altă monedă și la retragere.",
  },
  {
    term: "Taxă de custodie",
    group: "Costuri",
    definition:
      "Cost periodic pentru păstrarea instrumentelor financiare în cont, independent de numărul de tranzacții efectuate.",
  },
  {
    term: "Taxă de inactivitate",
    group: "Costuri",
    definition:
      "Cost aplicat conturilor fără activitate pe o perioadă definită în contract. Poate eroda soldurile mici.",
  },
  {
    term: "Rata totală de cheltuieli (TER)",
    group: "Costuri",
    definition:
      "Costul anual al administrării unui fond, exprimat procentual din activele administrate și reflectat automat în valoarea unității.",
  },
  {
    term: "Slippage",
    group: "Costuri",
    definition:
      "Diferența dintre prețul așteptat la plasarea unui ordin și prețul la care acesta se execută efectiv, frecventă în piețe volatile sau puțin lichide.",
  },

  // Risc
  {
    term: "Volatilitate",
    group: "Risc",
    definition:
      "Amplitudinea variațiilor de preț într-un interval de timp. Volatilitatea ridicată înseamnă atât potențial de câștig, cât și de pierdere mai mare.",
  },
  {
    term: "Risc de piață",
    group: "Risc",
    definition:
      "Riscul de pierdere cauzat de mișcarea generală a pieței, care nu poate fi eliminat prin diversificare.",
  },
  {
    term: "Risc de credit",
    group: "Risc",
    definition:
      "Riscul ca emitentul unui instrument de datorie să nu își onoreze obligațiile de plată.",
  },
  {
    term: "Risc de lichiditate",
    group: "Risc",
    definition:
      "Riscul de a nu putea vinde un activ rapid, la un preț apropiat de cel afișat, din lipsă de contrapartidă.",
  },
  {
    term: "Risc valutar",
    group: "Risc",
    definition:
      "Riscul ca variația cursului de schimb să modifice valoarea unei investiții denominate în altă monedă decât cea în care îți evaluezi averea.",
  },
  {
    term: "Efect de levier",
    group: "Risc",
    definition:
      "Utilizarea de fonduri împrumutate pentru a mări expunerea. Amplifică simetric atât câștigurile, cât și pierderile.",
  },
  {
    term: "Drawdown",
    group: "Risc",
    definition:
      "Scăderea de la un maxim istoric până la minimul ulterior. Măsoară pierderea maximă suportată de un investitor care a intrat la vârf.",
  },
  {
    term: "Toleranță la risc",
    group: "Risc",
    definition:
      "Capacitatea financiară și disponibilitatea psihologică de a suporta scăderi temporare fără a lichida pozițiile.",
  },

  // Piață și tranzacționare
  {
    term: "Bursă de valori",
    group: "Piață și tranzacționare",
    definition:
      "Piață reglementată pe care se tranzacționează instrumente financiare după reguli publice de listare, executare și transparență.",
  },
  {
    term: "Intermediar",
    group: "Piață și tranzacționare",
    definition:
      "Entitate autorizată prin care investitorul transmite ordine către piață. Statutul de autorizare se verifică în registrul autorității competente.",
  },
  {
    term: "Ordin la piață",
    group: "Piață și tranzacționare",
    definition:
      "Ordin executat la cel mai bun preț disponibil în momentul transmiterii. Asigură execuția, dar nu prețul.",
  },
  {
    term: "Ordin limită",
    group: "Piață și tranzacționare",
    definition:
      "Ordin executabil doar la un preț stabilit de investitor sau mai bun. Asigură prețul, dar nu execuția.",
  },
  {
    term: "Stop-loss",
    group: "Piață și tranzacționare",
    definition:
      "Ordin care se activează la atingerea unui preț prestabilit, pentru a limita pierderea. În piețe cu variații bruște, execuția poate avea loc la un preț mai slab.",
  },
  {
    term: "Lichiditate",
    group: "Piață și tranzacționare",
    definition:
      "Ușurința cu care un activ poate fi cumpărat sau vândut fără a-i afecta semnificativ prețul.",
  },
  {
    term: "Capitalizare bursieră",
    group: "Piață și tranzacționare",
    definition:
      "Valoarea totală de piață a acțiunilor unei societăți, calculată ca preț pe acțiune înmulțit cu numărul de acțiuni.",
  },
  {
    term: "Custodie",
    group: "Piață și tranzacționare",
    definition:
      "Păstrarea instrumentelor financiare ale clientului de către o entitate specializată. Modul de segregare a conturilor influențează protecția în caz de insolvență.",
  },

  // Criptoactive
  {
    term: "Criptoactiv",
    group: "Criptoactive",
    definition:
      "Reprezentare digitală a unei valori sau a unui drept, transferabilă și stocată electronic prin tehnologia registrelor distribuite.",
  },
  {
    term: "Portofel autocustodial",
    group: "Criptoactive",
    definition:
      "Portofel în care utilizatorul controlează direct cheile private. Pierderea frazei de recuperare duce la pierderea definitivă a accesului.",
  },
  {
    term: "Cheie privată",
    group: "Criptoactive",
    definition:
      "Secretul criptografic care permite semnarea tranzacțiilor și controlul asupra fondurilor asociate unei adrese.",
  },
  {
    term: "Stablecoin",
    group: "Criptoactive",
    definition:
      "Criptoactiv construit pentru a-și menține valoarea raportată la o monedă sau la un alt reper. Stabilitatea depinde de mecanismul și de rezervele emitentului.",
  },
  {
    term: "Taxă de rețea",
    group: "Criptoactive",
    definition:
      "Costul plătit rețelei blockchain pentru procesarea unei tranzacții. Variază în funcție de gradul de aglomerare al rețelei.",
  },

  // Credit și bancar
  {
    term: "Dobândă anuală efectivă (DAE)",
    group: "Credit și bancar",
    definition:
      "Indicator care exprimă costul total al unui credit pe an, incluzând dobânda și costurile obligatorii aferente. Permite compararea ofertelor.",
  },
  {
    term: "Anuitate",
    group: "Credit și bancar",
    definition:
      "Sistem de rambursare cu rată lunară constantă, în care ponderea dobânzii scade treptat, iar cea a principalului crește.",
  },
  {
    term: "Principal",
    group: "Credit și bancar",
    definition:
      "Suma împrumutată rămasă de rambursat, fără dobândă. Rambursările anticipate reduc principalul și, implicit, dobânda viitoare.",
  },
  {
    term: "Rambursare anticipată",
    group: "Credit și bancar",
    definition:
      "Plata unei părți din credit înainte de scadență, care poate reduce fie durata, fie rata lunară, conform contractului.",
  },
  {
    term: "Garantarea depozitelor",
    group: "Credit și bancar",
    definition:
      "Mecanism prin care depozitele constituite la instituții de credit participante sunt despăgubite în limitele și condițiile stabilite de fondul de garantare competent.",
  },
];

/** Romanian diacritics-safe slug used by the term detail routes. */
export function roTermSlug(term: string): string {
  return term
    .toLowerCase()
    .replace(/ă|â/g, "a")
    .replace(/î/g, "i")
    .replace(/ș|ş/g, "s")
    .replace(/ț|ţ/g, "t")
    .replace(/[()]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export interface RoGlossaryEntry extends RoGlossaryTerm {
  slug: string;
}

export const RO_GLOSSARY_ENTRIES: RoGlossaryEntry[] = RO_GLOSSARY.map((item) => ({
  ...item,
  slug: roTermSlug(item.term),
}));

export const RO_GLOSSARY_BY_SLUG: Record<string, RoGlossaryEntry> =
  Object.fromEntries(RO_GLOSSARY_ENTRIES.map((item) => [item.slug, item]));

/** Path of a glossary term detail page. */
export function roGlossaryTermPath(slug: string): string {
  return `/ro/dictionar-financiar/${slug}`;
}
